import { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";

const KEYS = {
    PHONE_NUMBER: "SUPPORT_PHONE_NUMBER",
    EMAIL: "SUPPORT_EMAIL",
    KAKAO_CHANNEL_URL: "SUPPORT_KAKAO_CHANNEL_URL",
    WORK_START_TIME: "SUPPORT_WORK_START_TIME",
    WORK_END_TIME: "SUPPORT_WORK_END_TIME",
    LUNCH_START_TIME: "SUPPORT_LUNCH_START_TIME",
    LUNCH_END_TIME: "SUPPORT_LUNCH_END_TIME",
};

function CustomerSupportSettingsScreen(): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // 연락처 설정
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [kakaoChannelUrl, setKakaoChannelUrl] = useState("");

    // 근무 시간 설정
    const [workStartTime, setWorkStartTime] = useState("");
    const [workEndTime, setWorkEndTime] = useState("");
    const [lunchStartTime, setLunchStartTime] = useState("");
    const [lunchEndTime, setLunchEndTime] = useState("");

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const [phoneRes, emailRes, kakaoRes, workStartRes, workEndRes, lunchStartRes, lunchEndRes] =
                await Promise.all([
                    request("SystemConfig.GetConfig", { paramz: { Key: KEYS.PHONE_NUMBER } }),
                    request("SystemConfig.GetConfig", { paramz: { Key: KEYS.EMAIL } }),
                    request("SystemConfig.GetConfig", { paramz: { Key: KEYS.KAKAO_CHANNEL_URL } }),
                    request("SystemConfig.GetConfig", { paramz: { Key: KEYS.WORK_START_TIME } }),
                    request("SystemConfig.GetConfig", { paramz: { Key: KEYS.WORK_END_TIME } }),
                    request("SystemConfig.GetConfig", { paramz: { Key: KEYS.LUNCH_START_TIME } }),
                    request("SystemConfig.GetConfig", { paramz: { Key: KEYS.LUNCH_END_TIME } }),
                ]);

            if (phoneRes?.data) setPhoneNumber(phoneRes.data.value || "");
            if (emailRes?.data) setEmail(emailRes.data.value || "");
            if (kakaoRes?.data) setKakaoChannelUrl(kakaoRes.data.value || "");
            if (workStartRes?.data) setWorkStartTime(workStartRes.data.value || "");
            if (workEndRes?.data) setWorkEndTime(workEndRes.data.value || "");
            if (lunchStartRes?.data) setLunchStartTime(lunchStartRes.data.value || "");
            if (lunchEndRes?.data) setLunchEndTime(lunchEndRes.data.value || "");
        } catch (error) {
            console.error("고객지원 설정 조회 실패:", error);
            setErrorMsg("설정을 불러오는데 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!window.confirm("설정을 저장하시겠습니까?")) return;

        setLoading(true);
        setSuccessMsg(null);
        setErrorMsg(null);

        try {
            await Promise.all([
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.PHONE_NUMBER, Value: phoneNumber, Description: "전화상담 전화번호" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.EMAIL, Value: email, Description: "1:1 문의 이메일" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.KAKAO_CHANNEL_URL, Value: kakaoChannelUrl, Description: "카카오채널 링크 주소" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.WORK_START_TIME, Value: workStartTime, Description: "근무시간 시작 (HH:mm)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.WORK_END_TIME, Value: workEndTime, Description: "근무시간 종료 (HH:mm)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.LUNCH_START_TIME, Value: lunchStartTime, Description: "점심시간 시작 (HH:mm)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.LUNCH_END_TIME, Value: lunchEndTime, Description: "점심시간 종료 (HH:mm)" } }),
            ]);

            setSuccessMsg("설정이 성공적으로 저장되었습니다.");
            setTimeout(() => setSuccessMsg(null), 3000);
        } catch (error) {
            console.error("고객지원 설정 저장 실패:", error);
            setErrorMsg("설정 저장 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            {/* 헤더 */}
                            <MDBox
                                mx={2} mt={-3} py={3} px={2}
                                variant="gradient" bgColor="info"
                                borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">
                                    고객지원 설정
                                </MDTypography>
                            </MDBox>

                            <MDBox p={3}>
                                {/* 알림 메시지 */}
                                {successMsg && (
                                    <MDBox mb={2}>
                                        <MDAlert color="success">{successMsg}</MDAlert>
                                    </MDBox>
                                )}
                                {errorMsg && (
                                    <MDBox mb={2}>
                                        <MDAlert color="error">{errorMsg}</MDAlert>
                                    </MDBox>
                                )}

                                {/* ── 연락처 설정 ── */}
                                <MDBox mb={2}>
                                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                                        연락처 설정
                                    </MDTypography>
                                </MDBox>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="전화상담 번호"
                                                fullWidth
                                                placeholder="예: 1588-1588"
                                                value={phoneNumber}
                                                onChange={(e: any) => setPhoneNumber(e.target.value)}
                                                helperText="고객 전화상담 대표 번호"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="1:1 문의 이메일"
                                                fullWidth
                                                placeholder="예: support@mediinstep.com"
                                                value={email}
                                                onChange={(e: any) => setEmail(e.target.value)}
                                                helperText="1:1 문의 수신 이메일 주소"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="카카오채널 URL"
                                                fullWidth
                                                placeholder="예: https://pf.kakao.com/_xxxxxx"
                                                value={kakaoChannelUrl}
                                                onChange={(e: any) => setKakaoChannelUrl(e.target.value)}
                                                helperText="카카오톡 채널 채팅 링크 주소"
                                            />
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider />

                                {/* ── 운영 시간 설정 ── */}
                                <MDBox mb={2}>
                                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                                        운영 시간 설정
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text">
                                        시간 형식: HH:mm (예: 09:00, 18:00)
                                    </MDTypography>
                                </MDBox>

                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <MDTypography variant="button" fontWeight="medium" color="text">
                                            근무 시간
                                        </MDTypography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="근무 시작 시간"
                                                fullWidth
                                                placeholder="09:00"
                                                value={workStartTime}
                                                onChange={(e: any) => setWorkStartTime(e.target.value)}
                                                helperText="평일 근무 시작 시간 (HH:mm)"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="근무 종료 시간"
                                                fullWidth
                                                placeholder="18:00"
                                                value={workEndTime}
                                                onChange={(e: any) => setWorkEndTime(e.target.value)}
                                                helperText="평일 근무 종료 시간 (HH:mm)"
                                            />
                                        </MDBox>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <MDTypography variant="button" fontWeight="medium" color="text">
                                            점심 시간
                                        </MDTypography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="점심 시작 시간"
                                                fullWidth
                                                placeholder="12:00"
                                                value={lunchStartTime}
                                                onChange={(e: any) => setLunchStartTime(e.target.value)}
                                                helperText="점심시간 시작 (HH:mm)"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="점심 종료 시간"
                                                fullWidth
                                                placeholder="13:00"
                                                value={lunchEndTime}
                                                onChange={(e: any) => setLunchEndTime(e.target.value)}
                                                helperText="점심시간 종료 (HH:mm)"
                                            />
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider />

                                {/* 저장 버튼 */}
                                <MDBox display="flex" justifyContent="flex-end" mt={2} mb={1}>
                                    <MDButton
                                        variant="gradient"
                                        color="info"
                                        onClick={handleSave}
                                        disabled={loading}
                                    >
                                        {loading ? "저장 중..." : "설정 저장"}
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
}

export default CustomerSupportSettingsScreen;
